import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Search, MapPin, FileText, Phone, DollarSign, ChevronRight, Shield, Book, Filter, X } from 'lucide-react';
import { Helmet } from 'react-helmet';
import GlobalNavigation from '../../components/GlobalNavigation';
import { supabase } from '../../lib/supabase';
import { searchBailSchedule, getBailScheduleStats, CATEGORY_NAMES } from '../../services/bailScheduleService';

export default function ToolsPage() {
  const [bailAmount, setBailAmount] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [preferredPayment, setPreferredPayment] = useState('');
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Bail Schedule State
  const [bailScheduleSearch, setBailScheduleSearch] = useState('');
  const [bailScheduleCategory, setBailScheduleCategory] = useState('');
  const [bailScheduleResults, setBailScheduleResults] = useState([]);
  const [isBailScheduleSearching, setIsBailScheduleSearching] = useState(false);
  const [bailScheduleStats, setBailScheduleStats] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchFacilities();
    fetchBailScheduleStats();
  }, []);

  const fetchFacilities = async () => {
    try {
      const { data, error } = await supabase
        ?.from('facilities')
        ?.select('*')
        ?.order('facility_name', { ascending: true });

      if (error) throw error;
      setFacilities(data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const fetchBailScheduleStats = async () => {
    const result = await getBailScheduleStats();
    if (result?.success) {
      setBailScheduleStats(result?.data || []);
    }
  };

  // Payment Calculator Logic
  const calculatePaymentPlans = () => {
    if (!bailAmount || parseFloat(bailAmount) <= 0) {
      alert('Please enter a valid bail amount');
      return;
    }

    const bail = parseFloat(bailAmount);
    const premium = bail * 0.10;
    const down = downPayment ? parseFloat(downPayment) : premium * 0.10;
    const remaining = premium - down;

    const plans = {
      premium: premium?.toFixed(2),
      downPayment: down?.toFixed(2),
      remaining: remaining?.toFixed(2),
      threeMonths: (remaining / 3)?.toFixed(2),
      sixMonths: (remaining / 6)?.toFixed(2),
      twelveMonths: (remaining / 12)?.toFixed(2),
      custom: preferredPayment ? Math.ceil(remaining / parseFloat(preferredPayment))?.toFixed(0) : null,
      customAmount: preferredPayment || null
    };

    setCalculatedResults(plans);
  };

  // Bail Cost Estimator
  const estimateBailCost = (amount) => {
    setBailAmount(amount);
    setTimeout(() => {
      const bail = parseFloat(amount);
      const premium = bail * 0.10;
      const down = premium * 0.10;
      const remaining = premium - down;

      const plans = {
        premium: premium?.toFixed(2),
        downPayment: down?.toFixed(2),
        remaining: remaining?.toFixed(2),
        threeMonths: (remaining / 3)?.toFixed(2),
        sixMonths: (remaining / 6)?.toFixed(2),
        twelveMonths: (remaining / 12)?.toFixed(2),
        custom: null,
        customAmount: null
      };

      setCalculatedResults(plans);
    }, 100);
  };

  // Inmate Search
  const handleInmateSearch = async () => {
    if (!searchQuery?.trim()) {
      alert('Please enter a name or booking number to search');
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        ?.from('persons')
        ?.select(`
          *,
          arrests (
            *,
            charges (*),
            facilities (*)
          )
        `)
        ?.or(`full_name.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
        ?.limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching inmates:', error);
      alert('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Bail Schedule Search
  const handleBailScheduleSearch = async () => {
    setIsBailScheduleSearching(true);
    try {
      const result = await searchBailSchedule({
        searchTerm: bailScheduleSearch || null,
        category: bailScheduleCategory || null
      });

      if (result?.success) {
        setBailScheduleResults(result?.data || []);
      } else {
        alert(result?.error || 'Search failed. Please try again.');
      }
    } catch (error) {
      console.error('Error searching bail schedule:', error);
      alert('Search failed. Please try again.');
    } finally {
      setIsBailScheduleSearching(false);
    }
  };

  const clearBailScheduleFilters = () => {
    setBailScheduleSearch('');
    setBailScheduleCategory('');
    setBailScheduleResults([]);
  };

  return (
    <>
      <Helmet>
        <title>Bail Bond Tools & Calculators | Sailors Bail Bonds</title>
        <meta
          name="description"
          content="Free bail bond calculators and tools - estimate costs, calculate payments, search inmates, locate county jails, and browse Sacramento County bail schedule."
        />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
        <GlobalNavigation onMenuClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Helpful Tools to Navigate Your Bail Process
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              Quick, easy-to-use tools to estimate costs, find inmates, and understand your options.
            </p>
          </div>
        </section>

        {/* Tools Grid */}
        <section className="pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto space-y-12">
            {/* Bail Schedule Database - NEW TOOL */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-yellow-300 shadow-lg shadow-yellow-300/20">
              <div className="flex items-center mb-6">
                <Book className="w-12 h-12 text-yellow-300 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-white">📚 Sacramento County Bail Schedule 2025</h2>
                  <p className="text-blue-100">Comprehensive list of criminal charges and bail amounts</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Search and Filter Controls */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-semibold mb-2">Search Charges</label>
                    <input
                      type="text"
                      value={bailScheduleSearch}
                      onChange={(e) => setBailScheduleSearch(e?.target?.value)}
                      placeholder="Search by code section or offense (e.g., PC 211, robbery)"
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-yellow-300"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Filter by Category</label>
                    <select
                      value={bailScheduleCategory}
                      onChange={(e) => setBailScheduleCategory(e?.target?.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white focus:outline-none focus:border-yellow-300"
                    >
                      <option value="">All Categories</option>
                      {Object?.entries(CATEGORY_NAMES)?.map(([key, name]) => (
                        <option key={key} value={key} className="bg-blue-900">{name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleBailScheduleSearch}
                    disabled={isBailScheduleSearching}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold py-4 rounded-full text-lg transition-all shadow-lg disabled:opacity-50"
                  >
                    {isBailScheduleSearching ? 'Searching...' : 'Search Bail Schedule'}
                  </button>
                  {(bailScheduleSearch || bailScheduleCategory) && (
                    <button
                      onClick={clearBailScheduleFilters}
                      className="px-6 py-4 bg-white/20 hover:bg-white/30 text-white font-bold rounded-full transition-all flex items-center"
                    >
                      <X className="w-5 h-5 mr-2" />
                      Clear
                    </button>
                  )}
                </div>

                {/* Statistics Summary */}
                {bailScheduleStats?.length > 0 && !bailScheduleResults?.length && (
                  <div className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20">
                    <h3 className="text-xl font-bold text-white mb-4">Bail Schedule Overview</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {bailScheduleStats?.map((stat) => (
                        <div key={stat?.category} className="bg-white/10 rounded-lg p-4">
                          <div className="text-sm text-blue-200">{CATEGORY_NAMES?.[stat?.category]}</div>
                          <div className="text-2xl font-bold text-white mt-1">{stat?.charge_count} Charges</div>
                          <div className="text-sm text-teal-300 mt-2">
                            Avg Bail: ${parseInt(stat?.avg_bail_amount)?.toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Results */}
                {bailScheduleResults?.length > 0 && (
                  <div className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-white">
                        Found {bailScheduleResults?.length} Charges
                      </h3>
                      <button
                        onClick={clearBailScheduleFilters}
                        className="text-teal-300 hover:text-teal-200 font-semibold flex items-center"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear Results
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto space-y-3">
                      {bailScheduleResults?.map((charge) => (
                        <div key={charge?.id} className="bg-white/10 rounded-lg p-4 border border-white/20 hover:border-yellow-300 transition-all">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-yellow-300 font-bold text-lg">{charge?.code_section}</div>
                              <div className="text-white mt-1">{charge?.offense_description}</div>
                              <div className="text-sm text-blue-200 mt-2">
                                Category: {CATEGORY_NAMES?.[charge?.category]}
                              </div>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-sm text-blue-200">Bail Amount</div>
                              <div className="text-2xl font-bold text-teal-300">
                                ${parseInt(charge?.bail_amount)?.toLocaleString()}
                              </div>
                              <button
                                onClick={() => estimateBailCost(charge?.bail_amount)}
                                className="mt-2 text-sm text-yellow-300 hover:text-yellow-200 underline"
                              >
                                Calculate Payment →
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bailScheduleResults?.length === 0 && (bailScheduleSearch || bailScheduleCategory) && !isBailScheduleSearching && (
                  <div className="text-center text-blue-200 py-8">
                    No charges found matching your search criteria. Try adjusting your filters.
                  </div>
                )}
              </div>

              <div className="mt-6 bg-blue-600/30 rounded-lg p-4 border border-blue-400/30">
                <p className="text-blue-100 text-sm">
                  <strong>Note:</strong> This bail schedule is for Sacramento County only and reflects 2025 presumptive bail amounts. 
                  Actual bail may vary based on individual circumstances, criminal history, and judicial discretion. 
                  Contact us for personalized assistance.
                </p>
              </div>
            </div>

            {/* Payment Calculator - PRIORITY TOOL */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 border-teal-300 shadow-lg shadow-teal-300/20">
              <div className="flex items-center mb-6">
                <Calculator className="w-12 h-12 text-teal-300 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-white">💰 Payment Calculator</h2>
                  <p className="text-blue-100">Calculate monthly payments based on bail amount</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-white font-semibold mb-2">Total Bail Amount ($)</label>
                    <input
                      type="number"
                      value={bailAmount}
                      onChange={(e) => setBailAmount(e?.target?.value)}
                      placeholder="Enter bail amount"
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-teal-300"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Down Payment Available ($)</label>
                    <input
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(e?.target?.value)}
                      placeholder="Optional - default is 10% of premium"
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-teal-300"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Preferred Monthly Payment ($)</label>
                    <input
                      type="number"
                      value={preferredPayment}
                      onChange={(e) => setPreferredPayment(e?.target?.value)}
                      placeholder="Optional"
                      className="w-full px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-teal-300"
                    />
                  </div>

                  <button
                    onClick={calculatePaymentPlans}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 rounded-full text-lg transition-all shadow-lg"
                  >
                    Calculate Payment Plans
                  </button>
                </div>

                {calculatedResults && (
                  <div className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20">
                    <h3 className="text-2xl font-bold text-white mb-4">Your Payment Options</h3>
                    <div className="space-y-4 text-white">
                      <div className="border-b border-white/20 pb-3">
                        <div className="text-blue-200">Total Premium Cost (10%)</div>
                        <div className="text-3xl font-bold text-teal-300">${calculatedResults?.premium}</div>
                      </div>
                      <div className="border-b border-white/20 pb-3">
                        <div className="text-blue-200">Down Payment</div>
                        <div className="text-2xl font-bold">${calculatedResults?.downPayment}</div>
                      </div>
                      <div className="border-b border-white/20 pb-3">
                        <div className="text-blue-200">Remaining Balance</div>
                        <div className="text-2xl font-bold">${calculatedResults?.remaining}</div>
                      </div>
                      <div className="space-y-2 pt-2">
                        <div className="flex justify-between">
                          <span className="text-blue-100">3 months:</span>
                          <span className="font-bold">${calculatedResults?.threeMonths}/month</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-100">6 months:</span>
                          <span className="font-bold">${calculatedResults?.sixMonths}/month</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-100">12 months:</span>
                          <span className="font-bold">${calculatedResults?.twelveMonths}/month</span>
                        </div>
                        {calculatedResults?.custom && (
                          <div className="flex justify-between border-t border-white/20 pt-2">
                            <span className="text-blue-100">Custom:</span>
                            <span className="font-bold">${calculatedResults?.customAmount}/month for {calculatedResults?.custom} months</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Link
                      to="/contact-us"
                      className="mt-6 w-full block text-center bg-white text-blue-900 hover:bg-blue-50 font-bold py-3 rounded-full transition-all"
                    >
                      Get Started with This Plan →
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Inmate Search Tool */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <div className="flex items-center mb-6">
                <Search className="w-12 h-12 text-teal-300 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-white">🔍 Inmate Search</h2>
                  <p className="text-blue-100">Search by name or booking number</p>
                </div>
              </div>

              <div className="max-w-2xl mx-auto">
                <div className="flex gap-4 mb-6">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Enter name or booking number..."
                    className="flex-1 px-4 py-3 rounded-lg bg-white/20 border-2 border-white/30 text-white placeholder-blue-200 focus:outline-none focus:border-teal-300"
                  />
                  <button
                    onClick={handleInmateSearch}
                    disabled={isSearching}
                    className="px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg transition-all disabled:opacity-50"
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>

                <div className="text-center mb-4">
                  <Link
                    to="/inmate-search-widget"
                    className="inline-flex items-center text-teal-300 hover:text-teal-200 font-semibold"
                  >
                    Or use our advanced inmate search page
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Link>
                </div>

                {searchResults?.length > 0 && (
                  <div className="space-y-4">
                    {searchResults?.map((person) => (
                      <div key={person?.id} className="bg-white/10 rounded-lg p-4 border border-white/20">
                        <div className="text-white font-bold text-lg">{person?.full_name}</div>
                        <div className="text-blue-100 text-sm mt-1">
                          {person?.arrests?.[0]?.facilities?.facility_name || 'Unknown Facility'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bail Cost Estimator */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <div className="flex items-center mb-6">
                <DollarSign className="w-12 h-12 text-teal-300 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-white">📊 Bail Cost Estimator</h2>
                  <p className="text-blue-100">Quick estimate of bail bond costs</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { amount: 10000, label: '$10,000 Bail' },
                  { amount: 25000, label: '$25,000 Bail' },
                  { amount: 50000, label: '$50,000 Bail' }
                ]?.map((preset) => (
                  <button
                    key={preset?.amount}
                    onClick={() => estimateBailCost(preset?.amount)}
                    className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20 hover:border-teal-300 transition-all text-white"
                  >
                    <div className="text-2xl font-bold mb-2">{preset?.label}</div>
                    <div className="text-blue-200">Premium: ${(preset?.amount * 0.10)?.toFixed(2)}</div>
                    <div className="text-sm text-teal-300 mt-2">Click to calculate payments →</div>
                  </button>
                ))}
              </div>
            </div>

            {/* County Jail Locator */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <div className="flex items-center mb-6">
                <MapPin className="w-12 h-12 text-teal-300 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold text-white">📍 County Jail Locator</h2>
                  <p className="text-blue-100">Find jails and facilities we serve</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {facilities?.map((facility) => (
                  <div key={facility?.facility_id} className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="flex items-center text-white">
                      <MapPin className="w-5 h-5 mr-2 text-teal-300" />
                      <span className="font-semibold">{facility?.facility_name}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <Link
                  to="/locations"
                  className="inline-flex items-center text-teal-300 hover:text-teal-200 font-semibold text-lg"
                >
                  View Detailed County Information
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>

            {/* More Resources */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Additional Resources</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Link
                  to="/5-step-bail-guide-with-faq"
                  className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20 hover:border-teal-300 transition-all group"
                >
                  <FileText className="w-10 h-10 text-teal-300 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Bail Process Guide</h3>
                  <p className="text-blue-100 mb-4">Step-by-step guide to the bail process</p>
                  <span className="text-teal-300 group-hover:text-teal-200 font-semibold">
                    Read Guide →
                  </span>
                </Link>

                <Link
                  to="/blog"
                  className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20 hover:border-teal-300 transition-all group"
                >
                  <FileText className="w-10 h-10 text-teal-300 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Knowledge Base</h3>
                  <p className="text-blue-100 mb-4">Articles and FAQs about bail bonds</p>
                  <span className="text-teal-300 group-hover:text-teal-200 font-semibold">
                    Browse Articles →
                  </span>
                </Link>

                <Link
                  to="/client-success-accountability-program-csap"
                  className="bg-gradient-to-br from-blue-600/30 to-teal-600/30 rounded-xl p-6 border border-white/20 hover:border-teal-300 transition-all group"
                >
                  <Shield className="w-10 h-10 text-teal-300 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">CSAP Program</h3>
                  <p className="text-blue-100 mb-4">Support and accountability services</p>
                  <span className="text-teal-300 group-hover:text-teal-200 font-semibold">
                    Learn More →
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-600/80 to-red-700/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Our experienced bail agents are available 24/7 to help you right now.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9162563468"
                className="inline-flex items-center justify-center bg-white text-red-600 hover:bg-red-50 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Phone className="w-6 h-6 mr-3" />
                Call (916) 256-3468
              </a>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center bg-red-700 hover:bg-red-800 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 border-2 border-white"
              >
                Contact Us Online
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}